import React, { useState, useCallback } from 'react';
import { parse, simplify, evaluate } from 'mathjs';
import lumiObserver from './lumiObserver';

// ── Theme (matches sibling primitives) ────────────────────────────────
const T = {
  bg: '#0D1117',
  surface: '#161B22',
  border: '#30363D',
  text: '#E6EDF3',
  textMid: '#8B949E',
  accent: '#0B8A7E',
  correct: '#10B981',
  wrong: '#EF4444',
  hint: '#F59E0B',
};

// ── Equivalence check ────────────────────────────────────────────────
// Given a string that may be a bare expression OR an equation ("lhs = rhs"),
// return a canonical simplified form suitable for string comparison.
function canonicalise(input) {
  if (!input || typeof input !== 'string') return null;
  const trimmed = input.trim();
  try {
    if (trimmed.includes('=')) {
      // Split only on the first '=' (safety: ignore double ==)
      const idx = trimmed.indexOf('=');
      const lhs = trimmed.slice(0, idx).trim();
      const rhs = trimmed.slice(idx + 1).trim();
      if (!lhs || !rhs) return null;
      const s = simplify(parse(`(${lhs}) - (${rhs})`));
      return { kind: 'equation', expr: s.toString(), lhs, rhs };
    }
    const s = simplify(parse(trimmed));
    return { kind: 'expression', expr: s.toString() };
  } catch (_) {
    return null;
  }
}

// Two algebraic strings are equivalent iff the simplified `f - g` evaluates
// to zero for every sample assignment of the common variables. For equations
// (lhs = rhs), we additionally accept a flipped equation (rhs = lhs) as
// equivalent — `2x = 10` is the same equation as `10 = 2x`. We also treat
// any non-zero *constant* multiple of the difference as equivalent
// (e.g. `2x = 10` is the same as `4x = 20`).
function exprsEquivalent(a, b) {
  const ca = canonicalise(a), cb = canonicalise(b);
  if (!ca || !cb) return false;
  if (ca.kind !== cb.kind) return false;

  const sampleScopes = [
    { x: 0, y: 1, t: 0, n: 0, a: 1, b: 1 },
    { x: 1, y: 2, t: 1, n: 1, a: 2, b: 3 },
    { x: -1, y: 3, t: -2, n: 2, a: -1, b: 0.5 },
    { x: 3.14, y: -2.5, t: 7, n: 5, a: 0.5, b: -3 },
    { x: -4.2, y: 0.7, t: 11, n: 3, a: 4, b: 2 },
  ];

  const evalAll = (expr) => sampleScopes.map(s => {
    try { return Number(evaluate(expr, s)); } catch { return NaN; }
  });

  if (ca.kind === 'expression') {
    // Equal iff (a - b) is identically zero on all samples
    try {
      const diff = `(${ca.expr}) - (${cb.expr})`;
      return evalAll(diff).every(v => Number.isFinite(v) && Math.abs(v) < 1e-6);
    } catch { return false; }
  }

  // Equations: compare the two canonical differences. They're equivalent if
  // one is a non-zero scalar multiple of the other (covers both sign flip
  // and scaling, e.g. "2x = 10" ≡ "10 = 2x" ≡ "4x = 20").
  try {
    const va = evalAll(ca.expr);
    const vb = evalAll(cb.expr);
    if (va.some(v => !Number.isFinite(v)) || vb.some(v => !Number.isFinite(v))) return false;

    // If both are identically zero everywhere, they're trivially equivalent
    // (e.g. `x = x` and `2x = 2x` both simplify to 0).
    const aAllZero = va.every(v => Math.abs(v) < 1e-9);
    const bAllZero = vb.every(v => Math.abs(v) < 1e-9);
    if (aAllZero && bAllZero) return true;
    if (aAllZero !== bAllZero) return false;

    // Find the first non-zero reference sample and compute the ratio;
    // the relationship holds only if every other sample matches.
    let ratio = null;
    for (let i = 0; i < va.length; i++) {
      if (Math.abs(va[i]) > 1e-9) {
        ratio = vb[i] / va[i];
        break;
      }
    }
    if (ratio === null || Math.abs(ratio) < 1e-9 || !Number.isFinite(ratio)) return false;
    return va.every((v, i) => Math.abs(vb[i] - ratio * v) < 1e-6 * Math.max(1, Math.abs(vb[i])));
  } catch { return false; }
}

// ── Specific hint generator ──────────────────────────────────────────
// Looks at the previous expression and the student's proposed next step,
// diagnoses common algebra mistakes, and returns a one-line hint.
function diagnoseError(previous, studentStep, expectedStep) {
  try {
    if (!previous || !studentStep) return null;
    const prev = canonicalise(previous);
    const stu = canonicalise(studentStep);
    const exp = canonicalise(expectedStep);
    if (!prev || !stu || !exp) return "I couldn't read that step — check for typos (missing operator or bracket)?";

    // Equations: compare simplified forms; if student's equation is NOT equivalent to the
    // previous equation, they've broken the equality — the most common cause is applying
    // an operation to only one side.
    if (prev.kind === 'equation' && stu.kind === 'equation') {
      if (!exprsEquivalent(previous, studentStep)) {
        // Check if student's result matches just the LHS change (one-side operation)
        const prevParts = previous.split('=').map(s => s.trim());
        const stuParts = studentStep.split('=').map(s => s.trim());
        if (prevParts.length === 2 && stuParts.length === 2) {
          const rhsSame = exprsEquivalent(prevParts[1], stuParts[1]);
          const lhsSame = exprsEquivalent(prevParts[0], stuParts[0]);
          if (rhsSame && !lhsSame) return "You changed the left side but not the right. Whatever you do to one side, you must do to the other.";
          if (lhsSame && !rhsSame) return "You changed the right side but not the left. Both sides must change by the same amount.";
        }
        return "That isn't equivalent to the previous line. Did you apply the same operation to both sides?";
      }
    }
    // Not equivalent to expected? Offer a gentle nudge.
    if (!exprsEquivalent(studentStep, expectedStep)) {
      return "You're on a valid path but that's not the next step we're looking for. Try to isolate the variable.";
    }
    return null;
  } catch (_) {
    return "I couldn't parse that — try again and double-check operators and brackets.";
  }
}

// ══════════════════════════════════════════════════════════════════════
// GUIDED SOLVER
// ══════════════════════════════════════════════════════════════════════
function GuidedSolver({ problem, solutionSteps, onComplete, onGiveUp, interactiveId }) {
  // solutionSteps starts with the problem at index 0; student proposes index 1, 2, ... in order.
  const [entered, setEntered] = useState([solutionSteps[0]?.expression || problem]);
  const [input, setInput] = useState('');
  const [attempts, setAttempts] = useState(0);
  const [message, setMessage] = useState('');
  const [messageKind, setMessageKind] = useState('idle'); // idle | wrong | hint | correct
  const [done, setDone] = useState(false);
  const [showSolution, setShowSolution] = useState(false);

  const stepIdx = entered.length; // next expected index

  const submit = () => {
    if (done) return;
    const trimmed = input.trim();
    if (!trimmed) return;
    const expected = solutionSteps[stepIdx];
    if (!expected) return;

    if (interactiveId) {
      lumiObserver.emit({ interactiveId, type: 'step_entered', payload: { step: stepIdx, input: trimmed } });
    }

    if (exprsEquivalent(trimmed, expected.expression)) {
      const nextEntered = [...entered, trimmed];
      setEntered(nextEntered);
      setInput('');
      setAttempts(0);
      if (interactiveId) {
        lumiObserver.emit({ interactiveId, type: 'step_accepted', payload: { step: stepIdx, input: trimmed } });
      }
      if (nextEntered.length === solutionSteps.length) {
        setDone(true);
        setMessage('You solved it.');
        setMessageKind('correct');
        onComplete?.({ steps: nextEntered });
        if (interactiveId) {
          lumiObserver.emit({ interactiveId, type: 'completed', payload: { steps: nextEntered.length, expectedActions: solutionSteps.length - 1 } });
        }
      } else {
        setMessage(`Correct — ${expected.action || 'on to the next step'}.`);
        setMessageKind('correct');
      }
    } else {
      const previous = entered[entered.length - 1];
      const diag = diagnoseError(previous, trimmed, expected.expression);
      const n = attempts + 1;
      setAttempts(n);
      setMessage(diag || "That isn't the next step. Keep trying.");
      setMessageKind('wrong');
      if (interactiveId) {
        // "almost" heuristic: they changed the right side but not the
        // left, or vice versa. Interpreter uses this for ABOUT_TO_SUCCEED.
        const almost = /changed.*left.*not.*right|changed.*right.*not.*left/i.test(diag || '');
        lumiObserver.emit({
          interactiveId, type: 'step_rejected',
          payload: { step: stepIdx, input: trimmed, previous, reason: diag || 'not equivalent', almost },
        });
      }
      if (n >= 3) {
        onGiveUp?.({ attempts: n });
        setShowSolution(true);
      }
    }
  };

  return (
    <div>
      <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 10 }}>{problem}</div>

      {/* Working stack */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
        {entered.map((line, i) => (
          <div key={i} style={{
            background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8,
            padding: '8px 12px', fontFamily: 'SF Mono, monospace', fontSize: 14,
            color: T.text, animation: 'fadeIn .25s ease',
          }}>
            <span style={{ color: T.textMid, marginRight: 8, fontSize: 11 }}>{i+1}.</span>
            {line}
            {i > 0 && solutionSteps[i]?.action && (
              <span style={{ color: T.textMid, marginLeft: 10, fontSize: 11, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                ← {solutionSteps[i].action}
              </span>
            )}
          </div>
        ))}
      </div>

      {!done && (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') submit(); }}
              placeholder="Type the next line…"
              style={{
                flex: 1, padding: '10px 12px', borderRadius: 8,
                background: T.bg, border: `1px solid ${T.border}`, color: T.text,
                fontFamily: 'SF Mono, monospace', fontSize: 14, outline: 'none',
              }}
            />
            <button onClick={submit}
              style={{ padding: '10px 16px', background: T.accent, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>
              Check
            </button>
          </div>

          {message && (
            <div style={{
              padding: '10px 14px', borderRadius: 8,
              background: messageKind === 'correct' ? T.correct + '22' : messageKind === 'wrong' ? T.wrong + '22' : T.hint + '22',
              border: `1px solid ${messageKind === 'correct' ? T.correct : messageKind === 'wrong' ? T.wrong : T.hint}`,
              color: messageKind === 'correct' ? T.correct : messageKind === 'wrong' ? T.wrong : T.hint,
              fontSize: 13, fontWeight: 600,
            }}>
              {messageKind === 'correct' ? '✓ ' : messageKind === 'wrong' ? '✗ ' : '💡 '}
              {message}
            </div>
          )}

          {showSolution && (
            <div style={{ marginTop: 10, padding: '10px 14px', borderRadius: 8, background: T.bg, border: `1px dashed ${T.border}`, fontSize: 12, color: T.textMid }}>
              <div style={{ fontWeight: 700, color: T.text, marginBottom: 4 }}>Full solution</div>
              {solutionSteps.map((s, i) => (
                <div key={i} style={{ fontFamily: 'SF Mono, monospace', marginBottom: 2 }}>
                  {i+1}. {s.expression}{s.action ? ` — ${s.action}` : ''}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// FREEFORM SOLVER
// ══════════════════════════════════════════════════════════════════════
function FreeformSolver({ problem, solutionSteps, onComplete, onGiveUp, interactiveId }) {
  // Student works through the problem line by line. We allow any valid path
  // (i.e. any line algebraically equivalent to the previous one) and
  // celebrate when they reach a form equivalent to the final step.
  const [lines, setLines] = useState([solutionSteps[0]?.expression || problem]);
  const [input, setInput] = useState('');
  const [attempts, setAttempts] = useState(0);
  const [message, setMessage] = useState('');
  const [messageKind, setMessageKind] = useState('idle');
  const [done, setDone] = useState(false);

  const finalExpr = solutionSteps[solutionSteps.length - 1]?.expression;

  const submit = () => {
    if (done) return;
    const trimmed = input.trim();
    if (!trimmed) return;
    if (interactiveId) {
      lumiObserver.emit({ interactiveId, type: 'step_entered', payload: { step: lines.length, input: trimmed } });
    }
    const last = lines[lines.length - 1];
    if (!exprsEquivalent(trimmed, last)) {
      const n = attempts + 1;
      setAttempts(n);
      const diag = diagnoseError(last, trimmed, finalExpr);
      setMessage(diag || `That isn't equivalent to the previous line (${last}). Double-check your operation.`);
      setMessageKind('wrong');
      if (interactiveId) {
        lumiObserver.emit({
          interactiveId, type: 'step_rejected',
          payload: { step: lines.length, input: trimmed, previous: last, reason: diag || 'not equivalent' },
        });
      }
      if (n >= 3) onGiveUp?.({ attempts: n });
      return;
    }
    // Valid algebraic step
    const next = [...lines, trimmed];
    setLines(next); setInput(''); setAttempts(0);
    if (interactiveId) {
      lumiObserver.emit({ interactiveId, type: 'step_accepted', payload: { step: lines.length, input: trimmed } });
    }

    if (finalExpr && exprsEquivalent(trimmed, finalExpr)) {
      setDone(true);
      setMessage('You reached the solution.');
      setMessageKind('correct');
      onComplete?.({ steps: next });
      if (interactiveId) {
        lumiObserver.emit({ interactiveId, type: 'completed', payload: { steps: next.length } });
      }
    } else {
      setMessage('Valid step — keep going.');
      setMessageKind('hint');
    }
  };

  return (
    <div>
      <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 10 }}>{problem}</div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
        {lines.map((line, i) => (
          <div key={i} style={{
            background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8,
            padding: '8px 12px', fontFamily: 'SF Mono, monospace', fontSize: 14,
            color: T.text,
          }}>
            <span style={{ color: T.textMid, marginRight: 8, fontSize: 11 }}>{i+1}.</span>
            {line}
          </div>
        ))}
      </div>

      {!done && (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') submit(); }}
              placeholder="Next line of working…"
              style={{
                flex: 1, padding: '10px 12px', borderRadius: 8,
                background: T.bg, border: `1px solid ${T.border}`, color: T.text,
                fontFamily: 'SF Mono, monospace', fontSize: 14, outline: 'none',
              }}
            />
            <button onClick={submit}
              style={{ padding: '10px 16px', background: T.accent, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>
              Check
            </button>
          </div>

          {message && (
            <div style={{
              padding: '10px 14px', borderRadius: 8,
              background: messageKind === 'correct' ? T.correct + '22' : messageKind === 'wrong' ? T.wrong + '22' : T.hint + '22',
              border: `1px solid ${messageKind === 'correct' ? T.correct : messageKind === 'wrong' ? T.wrong : T.hint}`,
              color: messageKind === 'correct' ? T.correct : messageKind === 'wrong' ? T.wrong : T.hint,
              fontSize: 13, fontWeight: 600,
            }}>
              {messageKind === 'correct' ? '✓ ' : messageKind === 'wrong' ? '✗ ' : '💡 '}
              {message}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// MAIN EXPORT — Dispatcher
// ══════════════════════════════════════════════════════════════════════
export default function Solver({
  problem,
  solutionSteps = [],
  mode = 'guided', // 'guided' | 'freeform'
  prompt,
  onComplete,
  onGiveUp,
  interactiveId,   // optional — when provided, emits observer events
}) {
  return (
    <div style={{
      background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14,
      padding: 16, fontFamily: "'Plus Jakarta Sans', sans-serif", color: T.text,
    }}>
      {prompt && (
        <div style={{ fontSize: 13, fontWeight: 700, color: T.textMid, marginBottom: 10 }}>{prompt}</div>
      )}
      {mode === 'freeform'
        ? <FreeformSolver problem={problem} solutionSteps={solutionSteps} onComplete={onComplete} onGiveUp={onGiveUp} interactiveId={interactiveId} />
        : <GuidedSolver   problem={problem} solutionSteps={solutionSteps} onComplete={onComplete} onGiveUp={onGiveUp} interactiveId={interactiveId} />
      }
    </div>
  );
}
